import { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AccountBalance, MaterialSymbol, PartnerType, Skeleton, SubPartnerAccount, Tooltip, withStyles } from 'vopay';
import { BodyText, Button, Card, Title } from 'UI';
import { useToast } from '@/providers/Toast.provider';
import { usePartnerData } from '@/providers/PartnerData.provider';
import { useModuleFeatureFlag } from '@/providers/FeatureFlag.provider';
import useFetchPartnerAccountStatusSummary from '@/components/Entities/hooks/useApi/useFetchPartnerAccountStatusSummary';
import AccountDetailsDrawer from '@/components/Entities/Accounts/AccountDetailsDrawer/AccountDetailsDrawer';
import AccountFilters from '@/components/Entities/Accounts/AccountFilters';
import AccountsTable from '@/components/Entities/Accounts/AccountsTable';
import useAccountsListing from '@/components/Entities/Accounts/useAccountsListing';
import { getAccountStatusTiles } from '@/components/Entities/Accounts/AccountsOverview/onboardingPipeline';
import * as accountPageRoute from '@/components/Entities/Accounts/accountPage.route';
import PartnerAccountCreation from '@/components/Entities/Accounts/AccountCreation/PartnerAccountCreation/PartnerAccountCreation';
import { canReadPartnerAccounts } from '@/components/Partners/PartnerManagement/partnerSelfScopedReads';
import styles from './PartnerAccountsTab.module.scss';

const cn = withStyles(styles);

type PartnerAccountsTabProps = {
  partner: SubPartnerAccount;
  /** From the partner page's details request; `''` until it has answered. */
  partnerType: PartnerType | '';
};

/**
 * The partner page's Accounts tab (spec 5.6.5, ID-13795): the Accounts page's own filters and table,
 * listing the accounts the partner account has opened, under a count of them and how many are
 * Active, with Add Account creating one under it. Every read and the creation are signed with the
 * partner account's own bearer, and those routes only answer an Active partner account — so one
 * still onboarding reads as not yet available rather than as an error. Chrome-less by design: the
 * tab card is the container.
 */
export default function PartnerAccountsTab({ partner, partnerType }: PartnerAccountsTabProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Partners.PartnerManagement.PartnerAccountsTab' });

  if (!canReadPartnerAccounts(partner)) {
    return (
      <BodyText className={cn('message')} data-testid="partnerAccountsInactive">
        {t('inactive', { defaultValue: 'Accounts become available once the partner account is Active.' })}
      </BodyText>
    );
  }

  // A row the list returned without a token cannot sign the reads: nothing the list rendered would
  // belong to this partner account, so the tab says so instead.
  if (!partner.Token) {
    return (
      <BodyText className={cn('message')} data-testid="partnerAccountsUnavailable">
        {t('unavailable', { defaultValue: "This partner account's accounts could not be loaded." })}
      </BodyText>
    );
  }

  // Keyed on the token so a new one remounts the list: the listing, the counts and any open drawer
  // all start over together rather than some following the new token and some the old.
  return (
    <PartnerAccountsList
      key={partner.Token}
      bearer={partner.Token}
      partnerName={partner.Name}
      partnerType={partnerType}
    />
  );
}

type PartnerAccountsListProps = {
  bearer: string;
  partnerName: string;
  partnerType: PartnerType | '';
};

function PartnerAccountsList({ bearer, partnerName, partnerType }: PartnerAccountsListProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Partners.PartnerManagement.PartnerAccountsTab' });
  const history = useHistory();
  const toast = useToast();

  const {
    response: { isInsideSales },
  } = usePartnerData();

  // The same gate as the Accounts page's Add Account, less the Viewer role, which never reaches this
  // Admin-only page. Inside Sales creates accounts through its own wizard with its own fields, so
  // neither an Inside Sales session nor an Inside Sales partner account gets this drawer — and the
  // partner account's type has to be known before that can be ruled out.
  const { canCreateAccount } = useModuleFeatureFlag('accounts');
  const addAccountPartnerType = partnerType === '' || partnerType === 'Inside Sales' ? null : partnerType;
  const canAddAccount = canCreateAccount && !isInsideSales && addAccountPartnerType !== null;

  const listing = useAccountsListing({
    bearer,
    onLoadError: (error) => {
      // The hook has logged the cause; without this an empty table would read as "no accounts".
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      toast.neutral(
        t('loadError', {
          defaultValue: "Could not load this partner account's accounts: {{errorMessage}}",
          errorMessage,
        }),
      );
    },
  });

  const { fetchingStatusSummary, numTotalAccounts, statusCounts, fetchPartnerAccountStatusSummary } =
    useFetchPartnerAccountStatusSummary({ bearer });

  // The Status filter offers what the Accounts page's tiles would (ID-13964 D3). The row carries only
  // the partner account's broad type, not the label a fixed pipeline keys on, so none is assumed: the
  // statuses its accounts are in, then the terminal ones.
  const filterStatuses = useMemo(() => getAccountStatusTiles('', false, statusCounts), [statusCounts]);

  // The hook's flag is false until the mount effect below runs; without this the cards would flash
  // zeroed counts for one frame first.
  const [hasLoadedStatusSummary, setHasLoadedStatusSummary] = useState(false);
  const [hasStatusSummaryFailed, setHasStatusSummaryFailed] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<AccountBalance | null>(null);
  const [isCreateDrawerOpen, setIsCreateDrawerOpen] = useState(false);

  const loadStatusSummary = () =>
    fetchPartnerAccountStatusSummary()
      .then(() => setHasStatusSummaryFailed(false))
      .catch((error) => {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';

        setHasStatusSummaryFailed(true);

        toast.neutral(
          t('summaryLoadError', { defaultValue: 'Could not load the account counts: {{errorMessage}}', errorMessage }),
        );
      })
      .finally(() => setHasLoadedStatusSummary(true));

  // Fetched on mount, and again once an account is added; the counts describe the whole book,
  // independent of the filters.
  useEffect(() => {
    loadStatusSummary();
  }, []);

  const isLoadingStatusSummary = fetchingStatusSummary || !hasLoadedStatusSummary;

  // A failed summary shows no count rather than a zero beside a populated table.
  const renderActiveCount = () => {
    if (isLoadingStatusSummary) return <Skeleton width={80} height={24} />;

    if (hasStatusSummaryFailed) return null;

    return (
      // Body/16/Medium in Figma `Green`
      <BodyText weight="md" className={cn('active')} data-testid="partnerAccountsActive">
        {t('active', { defaultValue: '{{count}} active', count: statusCounts.Active ?? 0 })}
      </BodyText>
    );
  };

  return (
    <div className={cn('tab')} data-testid="partnerAccountsTab">
      <div className={cn('heading')}>
        {/* H3/Large Title 24/Emphasized */}
        <Title level="3">{t('title', { defaultValue: 'Accounts' })}</Title>

        {/* Figma `Buttons`: Type="Primary"; Size="Standard"; Left Indicator="add" */}
        {canAddAccount && (
          <Button
            id="partnerAccounts-addAccount"
            colorScheme="primary"
            iconLeft="add"
            onClick={() => setIsCreateDrawerOpen(true)}
          >
            {t('addAccountButton', { defaultValue: 'Add Account' })}
          </Button>
        )}
      </div>

      <div className={cn('controls')}>
        <Card variant="section" className={cn('totalCard')} data-testid="partnerAccountsTotal">
          {isLoadingStatusSummary ? (
            <Skeleton width={40} height={46} />
          ) : (
            <span className={cn('total')}>{hasStatusSummaryFailed ? '—' : numTotalAccounts}</span>
          )}

          <div className={cn('labelRow')}>
            {/* Body/16/Regular, Text/Secondary75 */}
            <BodyText className={cn('secondaryText')}>{t('accounts', { defaultValue: 'Accounts' })}</BodyText>

            {/* Figma `info` (13:94) is the plain Material Symbol, not Tooltip's default circled button. */}
            <Tooltip
              type="hover"
              position="bottom"
              width="280px"
              hasButton={false}
              customElement={<MaterialSymbol icon="info" size="16px" color="rgba(45, 45, 45, 0.5)" />}
              information={t('totalTooltip', {
                defaultValue: 'All accounts and sub-accounts this partner account has opened.',
              })}
            />
          </div>
        </Card>

        <Card variant="section" className={cn('filtersCard')}>
          <AccountFilters listing={listing} statuses={filterStatuses} summary={renderActiveCount()} isInCard />
        </Card>
      </div>

      <AccountsTable listing={listing} hasPartnerColumn={false} onAccountClick={setSelectedAccount} />

      {isCreateDrawerOpen && addAccountPartnerType && (
        <PartnerAccountCreation
          partnerAccount={{ bearer, name: partnerName, partnerType: addAccountPartnerType }}
          onClose={() => setIsCreateDrawerOpen(false)}
          onCreated={() => {
            listing.loadAccounts();
            loadStatusSummary();
          }}
        />
      )}

      <AccountDetailsDrawer
        account={selectedAccount}
        onClose={() => setSelectedAccount(null)}
        onViewFullDetails={(account) =>
          history.push(accountPageRoute.getPath({ accountID: account.AccountID, currency: account.Currency }))
        }
      />
    </div>
  );
}
